import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react";
import { getClient } from "@/features/clients/api";
import type { Client } from "@/features/clients/types";
import { ApiError } from "@/lib/api";

interface UseClientResult {
  client: Client | null;
  loading: boolean;
  /** Human-readable error, or null. `notFound` is true specifically for a 404. */
  error: string | null;
  notFound: boolean;
  /**
   * Push a fresh client into state — e.g. after a profile PATCH returns the
   * updated record — so the layout header re-renders without a refetch.
   */
  setClient: Dispatch<SetStateAction<Client | null>>;
}

/**
 * Fetches a single client by id for the detail pages. StrictMode-safe (skips a
 * duplicate fetch for the same id) with an out-of-order guard so a slow earlier
 * request can't clobber a newer one.
 */
export function useClient(id: string | undefined): UseClientResult {
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [trackedId, setTrackedId] = useState(id);

  const lastIdRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  // The detail layout stays mounted as :id changes (A → B), so reset to a clean
  // loading state the instant the id changes — otherwise the previous client's
  // data would render for a frame under the new id (a real trust bug in a tax
  // app). Adjusting state during render re-renders before the browser paints.
  if (id !== trackedId) {
    setTrackedId(id);
    setClient(null);
    setError(null);
    setNotFound(false);
    setLoading(true);
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setClient(null);
      setLoading(false);
      setNotFound(true);
      return;
    }
    if (lastIdRef.current === id) return; // same id (incl. StrictMode) → skip
    lastIdRef.current = id;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setNotFound(false);

    getClient(id)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setClient(result);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setClient(null);
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError(err instanceof ApiError ? err.message : "Failed to load client.");
        }
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [id]);

  return { client, loading, error, notFound, setClient };
}
